import { afterNextRender, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, lastValueFrom, map } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { ApiResponse, Metadata } from '../../../../api/api-types/api-response.api';
import {
	Assignment,
	AssignmentEndOfCare,
	AssingmentImpedimentReportMinMaxDueDate,
	UpdateAssignmentBody,
} from '../../../../api/app-types/assignment';
import { CaseGroup } from '../../../../api/app-types/case-group';
import { DisabilityKind } from '../../../../api/app-types/disability-type.app';
import { LegalFoundation } from '../../../../api/app-types/legal-foundation.app';
import { PersonType } from '../../../../api/app-types/person-type.app';
import { DB_PHASES } from '../../../../api/app-types/phase.app';
import { Sponsor } from '../../../../api/app-types/sponsor.app';
import { NO_TYPE_OF_CARE, TypeOfCare } from '../../../../api/app-types/type-of-care';
import { ASSIGNMENT_API } from '../../../../api/services/assignment.service';
import { LEGAL_FOUNDATION_SERVICE, provideLegalFoundationApi } from '../../../../api/services/legal-foundation.service';
import { provideSponsorApi, SPONSOR_SERVICE } from '../../../../api/services/sponsor.service';
import {
	mapEditFormFieldNamesToApiAssignmentFieldNames,
	transformAssignmentToUpdateAssignmentBody,
} from '../../../../api/transformers/assignment.transform';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrSection } from '../../../../core/components/section/section';
import { LvrSectionForm } from '../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { STATE } from '../../../../core/services/state.service';
import { TENANT_FEATURES } from '../../../../core/services/tenant-feature.service';
import { compareById } from '../../../../shared/helpers/compare-by-id.func';
import { matDateProviders, oldestPossibleDateForPhaseChange } from '../../../../shared/helpers/date.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../../shared/validators/max-date-validator';
import { TypeOfCareChangeDialog } from './type-of-care-change.dialog';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { DatePipe } from '@angular/common';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { DocumentFile, DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { Client } from '../../../../api/app-types/client.app';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { hasError, hasFileError } from '../../../../shared/helpers/has-error.func';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { Lek } from '../../../../api/app-types/lek.app';
import { ClarificationValue } from '../../../../api/app-types/relative.app';
import { LvrDataProtection } from '../../../../core/components/data-protection/data-protection';
import { RouterLink } from '@angular/router';
import { VALIDATION_SERVICE } from '../../../../api/services/validation.service';
import { DateRange } from '../../../../api/app-types/validation.app';

@Component({
	selector: 'lvr-assignment-edit',
	templateUrl: './assignment-edit.html',
	styleUrl: './assignment-edit.scss',
	imports: [
		TranslocoPipe,
		ReactiveFormsModule,
		LvrPaddingDirective,
		LvrField,
		MatFormFieldModule,
		MatSelectModule,
		LvrAutoSaveDirective,
		LvrInputDirective,
		MatInput,
		MatDatepickerModule,
		LvrRestoreActions,
		LvrDefaultActions,
		LvrSection,
		LvrSectionForm,
		LvrSearchableSelect,
		LvrHeading,
		DatePipe,
		LvrAppendAsteriskPipe,
		DocumentUploadControl,
		LvrDataProtection,
		RouterLink,
	],
	providers: [matDateProviders, provideSponsorApi, provideLegalFoundationApi],
})
export class AssignmentEdit extends LvrRedirectNav {
	private readonly dialog = inject(MatDialog);
	private readonly validationApi = inject(VALIDATION_SERVICE);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	public readonly legalBaseRequiredInP2Enabled = this.tenantFeatures.legalBaseRequiredInP2Enabled();
	public readonly careRelatedDisabilityTypeRequiredInP2Enabled =
		this.tenantFeatures.careRelatedDisabilityTypeRequiredInP2Enabled();
	private readonly clientResolved = this.resolveDataAsSignal<ClientResolved | undefined>('client');
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	private readonly assignmentData =
		this.resolveDataAsSignal<ApiResponse<Assignment | undefined, Metadata>>('assignmentData');
	public readonly assignment = computed(() => this.assignmentData()?.data);
	public readonly assignmentId = computed(() => this.assignment()?.id ?? -1);
	private readonly impedimentDateRange = this.resolveDataAsSignal<AssingmentImpedimentReportMinMaxDueDate>('dateRange');
	private readonly disabilityKindsPage =
		this.resolveDataAsSignal<PaginatedResponse<DisabilityKind>>('disabilityKindsPage');
	public readonly disabilityKinds = computed(() => (this.disabilityKindsPage()?.entities ?? []) as DisabilityKind[]);
	private readonly typesOfCarePage = this.resolveDataAsSignal<PaginatedResponse<TypeOfCare>>('typesOfCare');
	private readonly caseGroupPage = this.resolveDataAsSignal<PaginatedResponse<CaseGroup>>('caseGroups');
	private readonly endOfCarePage = this.resolveDataAsSignal<PaginatedResponse<AssignmentEndOfCare>>('endOfCares');
	public readonly endOfCares = computed(() => this.endOfCarePage()?.entities ?? []);
	private readonly initiatorPage = this.resolveDataAsSignal<PaginatedResponse<PersonType>>('institutions');
	private readonly lekPage = this.resolveDataAsSignal<PaginatedResponse<Lek>>('lekPage');
	private readonly leks = computed(() => this.lekPage()?.entities);
	private readonly hasUnfinishedReport = this.resolveDataAsSignal<boolean>('hasUnfinishedReport');
	public readonly typesOfCare = computed(() => this.typesOfCarePage()?.entities ?? []);
	public readonly caseGroups = computed(() => this.caseGroupPage()?.entities ?? []);
	public readonly initiators = computed(() => this.initiatorPage()?.entities ?? []);
	public readonly initiatorLabel = (option: PersonType) => option.name;
	public readonly caseGroupLabel = (option: CaseGroup) => option.description;
	public readonly sponsorLabel = (option: Sponsor) => option.name;
	public readonly legalFoundationLabel = (option: LegalFoundation) => option.description;

	public readonly isLoading = signal<boolean>(false);
	private readonly draftService = inject(FormPersistenceService);
	private readonly assignmentApi = inject(ASSIGNMENT_API);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly sponsorService = inject(SPONSOR_SERVICE);
	private readonly state = inject(STATE);
	private readonly legalFoundationService = inject(LEGAL_FOUNDATION_SERVICE);
	private readonly session = inject(SessionManagerService);
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly showCaseGroup = computed(() => this.tenantFeatures.caseGroupEnabled());

	public readonly DB_PHASES = DB_PHASES;
	public readonly fromPhaseId = computed(() => this.assignment()?.phaseId ?? null);
	public readonly targetPhaseId = computed(() => {
		const a = this.assignment();
		if (!a) return null;

		// Active phase transition
		if (a.phaseChangeId != null) return a.phaseChangeId;

		// Active draft
		if (this.isValidDraft()) return a.draftPhaseId;

		// Current phase
		return a.phaseId;
	});

	public readonly isValidDraft = computed(() => {
		const a = this.assignment();
		if (!a) return false;
		return (
			a.phaseId === DB_PHASES.P1 &&
			a.draftPhaseId != null &&
			(a.draftPhaseId === DB_PHASES.P2 || a.draftPhaseId === DB_PHASES.P3)
		);
	});

	public readonly showP2Fields = computed(() => {
		const to = this.targetPhaseId();
		return to === DB_PHASES.P2 || to === DB_PHASES.P3;
	});
	public readonly showConsultationStart = computed(() => this.targetPhaseId() === DB_PHASES.P2);
	public readonly showP3Fields = computed(() => this.targetPhaseId() === DB_PHASES.P3);
	public readonly showP4Fields = computed(() => this.targetPhaseId() === DB_PHASES.P4);

	public readonly showRequiredLegalBaseInP2 = computed(
		() => this.legalBaseRequiredInP2Enabled && this.targetPhaseId() === DB_PHASES.P2,
	);
	public readonly showCareRelatedDisabilityTypeInP2 = computed(
		() => this.careRelatedDisabilityTypeRequiredInP2Enabled && this.targetPhaseId() === DB_PHASES.P2,
	);

	private shouldIgnoreImpedimentReportDueDate(): boolean {
		return this.targetPhaseId() === DB_PHASES.P4 && this.assignment()?.phaseId === DB_PHASES.P3;
	}

	public readonly readOnlyStartDate = computed(() => {
		const a = this.assignment();
		if (!a) return null;

		switch (a.phase?.remark) {
			case 'P1':
				return a.contactStart;
			case 'P2':
				return a.consultationDate;
			case 'P3':
				return a.careStartDate;
			default:
				return a.contactStart;
		}
	});

	public readonly readOnlyStartDateLabel = computed(() => {
		const a = this.assignment();
		if (!a) return null;

		switch (a.phase?.remark) {
			case 'P1':
				return 'assignment.details.contact_phase_start';
			case 'P2':
				return 'assignment.details.consultation_start';
			case 'P3':
				return 'assignment.details.care_start';
			default:
				return 'assignment.details.contact_phase_start';
		}
	});

	public readonly draftId = computed(() => {
		return `${FORM_KEYS.EDIT_ASSIGNMENT}.${this.assignmentId()}`;
	});

	public readonly matcher = new ImmediateErrorStateMatcher();

	public readonly availableSponsor = signal<Sponsor[]>([]);
	public readonly availableLegalFoundation = signal<LegalFoundation[]>([]);

	public readonly maxDate = signal(new Date());
	public readonly minDatePhaseStart = computed(() => {
		const max = this.setDateToMidnight(this.maxDate());
		const min = new Date(max);
		min.setDate(max.getDate() - this.dateWarningRange.max);
		return min;
	});

	public readonly minDateIncapacityForWorkSince = computed(() => {
		const max = this.setDateToMidnight(this.maxDate());
		const min = new Date(max);
		min.setFullYear(max.getFullYear() - this.incapacityForWorkLimitYears);
		return min;
	});

	public readonly minDateP2 = computed(() => {
		return oldestPossibleDateForPhaseChange(
			DB_PHASES.P2,
			this.assignment(),
			this.state.previousAssignmentBeforeCurrent(),
		);
	});

	public readonly minDateP3 = computed(() => {
		return oldestPossibleDateForPhaseChange(
			DB_PHASES.P3,
			this.assignment(),
			this.state.previousAssignmentBeforeCurrent(),
		);
	});

	public readonly minImpedimentReportDueDate = computed(() => {
		const minDate = this.impedimentDateRange()?.minStartDate;
		minDate?.setHours(0, 0, 0, 0);
		return minDate;
	});

	public readonly maxImpedimentReportDueDate = computed(() => this.impedimentDateRange()?.maxEndDate);
	public readonly minDateP4 = computed(() => {
		return oldestPossibleDateForPhaseChange(
			DB_PHASES.P4,
			this.assignment(),
			this.state.previousAssignmentBeforeCurrent(),
			this.leks(),
		);
	});

	public readonly clarificationDateRange = signal<DateRange | undefined>(undefined);
	public readonly clarificationMinDate = computed(() => {
		return this.clarificationDateRange()?.min;
	});
	public readonly clarificationMaxDate = computed(() => {
		return this.clarificationDateRange()?.max;
	});

	public readonly environment = inject(ENVIRONMENT);
	public readonly subjectType = DocumentFileSubjectType.ASSIGNMENT;
	public readonly canEditAssignmentsPermission = hasPermission(this.session.permissions, [
		'Assignments.Edit',
		'Assignments.Edit.PhaseOneOnly',
	]);
	public readonly canManageAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Edit');
	public readonly maxFileCount = this.environment.contactSettings.maxFiles;
	public readonly maxFileSize = this.environment.fileSettings.maxFileSize;
	public readonly allowedFileTypes = this.environment.fileSettings.allowedMimeTypes;
	public readonly form = this.initAssignmentForm(inject(FormBuilder), this.assignment());

	private readonly typeOfCareId = toSignal(
		this.form.get('typeOfCareId')!.valueChanges.pipe(
			map((val) => val),
			distinctUntilChanged(),
		),
		{
			initialValue: this.assignment()?.typeOfCareId,
		},
	);

	public readonly compareById = compareById;
	public readonly dateFormatHint = this.environment.visualDateFormat;
	public readonly incapacityForWorkLimitYears = this.environment.incapacityForWorkLimitYears;
	public readonly dateWarningRange = {
		min: this.environment.formConfiguration.contact.dateWarningRange.min,
		max: this.environment.formConfiguration.contact.dateWarningRange.max, // days retroactively
	};

	private readonly sponsorId = toSignal(
		this.form.get('sponsorId')!.valueChanges.pipe(
			map((val) => val),
			distinctUntilChanged(),
		),
		{
			initialValue: this.assignment()?.sponsorId,
		},
	);

	public readonly saveDisabled = signal(true);

	public readonly queryParams = {
		redirectUrl: this.router.url,
	};

	public constructor() {
		super();
		afterNextRender(() => {
			this.saveDisabled.set(this.form.invalid);
			this.form.statusChanges.subscribe(() => {
				this.saveDisabled.set(this.form.invalid);
			});
			// Show validation of date controls
			this.markInvalidDatesAsTouched();
		});

		effect(() => {
			const typeOfCareId = this.typeOfCareId();
			const sponsorId = this.sponsorId();
			this.updateFormControls(typeOfCareId, sponsorId);
			this.getFilteredLegalFoundations(typeOfCareId, sponsorId);
		});

		effect(() => {
			const typeOfCareId = this.typeOfCareId();
			if (!typeOfCareId) return;
			this.getSponsors(typeOfCareId);
		});

		effect(() => {
			const kinds = this.disabilityKinds();
			if (kinds.length > 0) return;
			this.form.get('careRelatedHandicapId')?.disable();
		});

		effect(() => {
			const endOfCares = this.endOfCares();
			if (endOfCares.length > 0) return;
			this.form.get('endOfCareId')?.disable();
		});

		/**
		 * This effects reacts on the metadata coming from the resolver
		 * and enables/disables form controls based on the editOnlyFields array.
		 * If the array contains '*', all fields are editable.
		 */
		effect(() => {
			const meta = this.assignmentData()?.metadata?.editOnlyFields ?? [];
			if (meta.includes('*')) return;
			Object.keys(this.form.controls).forEach((key) => {
				const control = this.form.get(key);
				const metaValueOfControl = mapEditFormFieldNamesToApiAssignmentFieldNames(key);
				if (control) {
					const isEnabled = meta.some((field) => field.toLowerCase() === metaValueOfControl.toLowerCase());
					if (isEnabled) {
						control.enable();
					} else {
						control.disable();
					}
				}
			});
		});

		// Reset validators on phase change
		effect(() => {
			this.targetPhaseId();
			this.fromPhaseId();
			this.applyValidatorsForTransition();
		});

		// Valid Clarification Date
		effect(() => {
			const assignmentId = this.assignmentId();
			if (!assignmentId) return;
			void this.loadValidClarificationDateRange(assignmentId);
		});
	}

	/**
	 * Checks if a form control has a specific validation error and has been touched.
	 * Used for LVR-Error.
	 * @param control The form control to check.
	 * @param errors The name errors to look for.
	 * @returns True if the any error exists and the control was touched.
	 */
	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	public hasFileError = (control: AbstractControl | null, ...errors: string[]) => hasFileError(control, ...errors);

	public saveDraftBeforeNavigation() {
		this.draftService.saveDraft('assignment', this.draftId(), this.form.getRawValue());
	}

	public getDisabilityKindName(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.disabilityType?.remark ?? '-';
	}

	public getDisabilityKindDifferentiation(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.categoryDiagnosis?.description ?? '-';
	}

	/**
	 * Cancel edit mode and redirecting to the detail view again.
	 */
	public cancel() {
		const client = this.client();
		if (!client) return;

		this.draftService.deleteDraft('assignment', this.draftId());
		this.navigateAfterFinished(['/desk/client', client.id, 'assignments', this.assignmentId()]);
	}

	/**
	 * Trigger the update on the API.
	 */
	public async editAssignment() {
		if (this.isLoading()) return;

		const client = this.client();
		if (!client) return;

		const rawValue = this.form.getRawValue();
		const { clarification, documents, impedimentReportDueDate, ...restFormValue } = rawValue;

		const clar = clarification ?? {
			hasBeenClarified: false,
			clarifiedAt: undefined,
			typeOfClarification: undefined,
		};

		const docs = (documents ?? []) as DocumentFile[];
		const docIds = docs.map((d: DocumentFile) => d.id);

		const originalAssignment = this.assignment();
		if (!originalAssignment) return;
		const body = transformAssignmentToUpdateAssignmentBody(originalAssignment);

		const isLegacyClarification = this.isLegacyClarification(originalAssignment);
		const payload: UpdateAssignmentBody = {
			...body,
			...restFormValue,
			documentIds: docIds,
			socialDataProtectionInfo: isLegacyClarification
				? originalAssignment.socialDataProtectionInfo
				: clar.hasBeenClarified,
			hasBeenClarified: clar.hasBeenClarified,
			clarifiedAt: clar.hasBeenClarified ? clar.clarifiedAt : undefined,
			typeOfClarification: clar.hasBeenClarified ? clar.typeOfClarification : undefined,
		};

		if (this.shouldIgnoreImpedimentReportDueDate()) {
			delete payload.impedimentReportDueDate;
		} else {
			payload.impedimentReportDueDate = impedimentReportDueDate;
		}

		const typeOfCareCtrl = this.form.get('typeOfCareId');
		const typeOfCareId = typeOfCareCtrl?.enabled ? typeOfCareCtrl.value : originalAssignment.typeOfCareId;

		this.isLoading.set(true);

		try {
			const canProceed = await this.checkForTypeOfCareChangeIfWeCanProceed(
				originalAssignment.typeOfCareId,
				typeOfCareId,
			);
			if (!canProceed) return;

			await this.assignmentApi.update(client.id, this.assignmentId(), payload);
			await this.state.updateAssignments();
			await this.navigateAfterFinished(['/desk/client', client.id, 'assignments', this.assignmentId()]);
			this.draftService.deleteDraft('assignment', this.draftId());
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.assignment_edited')),
			);
		} catch (e) {
			console.error(e);
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.assignment_edit_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Checks if the given date string is valid and different from the default DateOnly.MinValue ('0001-01-01'),
	 * which is returned by the backend when no date is provided.
	 * @param input - Date string to validate.
	 * @returns true if the date is valid and not the default min value.
	 */
	public isValidDate(input?: Date | string) {
		if (!input) {
			return false;
		}

		const date = new Date(input);
		if (Number.isNaN(date.getTime())) {
			return false;
		}

		// Check if it's the default '0001-01-01'
		const isMinDate = date.getFullYear() === 1 && date.getMonth() === 0 && date.getDate() === 1;

		return !isMinDate;
	}

	/**
	 * Gets the available data and sets each result into its corresponding signal.
	 */
	private async getSponsors(typeOfCareId?: number) {
		const sponsors = (await this.sponsorService.getAllSponsorsForTypeOfCare(typeOfCareId ?? NO_TYPE_OF_CARE)).entities;
		this.availableSponsor.set(sponsors);
	}

	/**
	 * Gets the legal foundations for the current integration office.
	 * @returns A filtered legal foundation list.
	 */
	private async getFilteredLegalFoundations(typeOfCareId?: number, sponsorId?: number) {
		if (!sponsorId) {
			this.availableLegalFoundation.set([]);
			return;
		}

		typeOfCareId = typeOfCareId ?? NO_TYPE_OF_CARE;

		const res = await this.legalFoundationService.getAllForTypeOfCareAndSponsor(typeOfCareId, sponsorId);
		this.availableLegalFoundation.set(res.entities);
	}

	private updateFormControls(typeOfCareId?: number, sponsorId?: number) {
		if (!typeOfCareId && !sponsorId) {
			this.form.get('sponsorId')?.disable();
			this.form.get('legalFoundationId')?.disable();
			this.form.get('sponsorId')?.setValue(undefined);
			this.form.get('legalFoundationId')?.setValue(undefined);
			return;
		} else if (typeOfCareId && !sponsorId) {
			this.form.get('sponsorId')?.enable();
		} else if (typeOfCareId && sponsorId) {
			this.form.get('sponsorId')?.enable();
			this.form.get('legalFoundationId')?.enable();
		}
	}

	/**
	 * Sets a new Date to the start of the midnight (00:00:00).
	 * @param date A Date.
	 * @returns A Date set at midnight.
	 */
	private setDateToMidnight(date: Date) {
		const dateAtMidnight = new Date(date);
		dateAtMidnight.setHours(0, 0, 0, 0);
		return dateAtMidnight;
	}

	/**
	 * Initializes the form for creating a new Assignment.
	 */
	private initAssignmentForm(fb: FormBuilder, assignment?: Assignment): FormGroup {
		const docs = Array.isArray(assignment?.documents) ? assignment!.documents : [];

		return fb.group({
			typeOfCareId: fb.control(assignment?.typeOfCareId),
			sponsorId: fb.control({ value: assignment?.sponsorId, disabled: true }),
			legalFoundationId: fb.control({ value: assignment?.legalFoundationId, disabled: true }),
			caseGroupId: fb.control(assignment?.caseGroupId),
			careRelatedHandicapId: fb.control(assignment?.careRelatedHandicapId),
			initiatorId: fb.control(assignment?.initiatorId),
			unemployabilitySince: fb.control(assignment?.unemployabilitySince),
			otherText: fb.control(assignment?.otherText),
			consultationStartDate: fb.control(assignment?.consultationDate),
			careStartDate: fb.control(assignment?.careStartDate),
			endOfCareId: fb.control(assignment?.endOfCare?.id),
			cancellationDate: fb.control(assignment?.cancellationDate),
			documents: fb.control<DocumentFile[]>(docs, [
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.allowedFileTypes),
			]),
			impedimentReportDueDate: fb.control(assignment?.impedimentReportDueDate),
			clarification: fb.control<ClarificationValue>(this.getEffectiveClarification(assignment)),
		});
	}

	private async checkForTypeOfCareChangeIfWeCanProceed(currentTypeOfCareId?: number, newTypeOfCareId?: number) {
		if (
			!currentTypeOfCareId ||
			!this.hasUnfinishedReport() ||
			currentTypeOfCareId.toString().trim() === newTypeOfCareId?.toString().trim()
		) {
			return true;
		}

		const dialog = await lastValueFrom(
			this.dialog
				.open(TypeOfCareChangeDialog, {
					maxWidth: '600px',
				})
				.afterClosed(),
		);
		return dialog;
	}

	private readonly impedimentReportDueDateValue = toSignal(this.form.get('impedimentReportDueDate')!.valueChanges, {
		initialValue: this.form.get('impedimentReportDueDate')!.value,
	});

	public readonly isImpedimentReportDueDateDirty = computed(() => {
		this.impedimentReportDueDateValue();

		return !!this.form.get('impedimentReportDueDate')?.dirty;
	});

	private validateOnlyWhenDirty(validators: ValidatorFn[]): ValidatorFn {
		return (control: AbstractControl) => {
			if (!control.dirty) {
				return null;
			}

			return Validators.compose(validators)?.(control) ?? null;
		};
	}

	private baseValidators(): Record<string, ValidatorFn[]> {
		return {
			initiatorId: [],
			clarification: [],
			consultationStartDate: [],
			careStartDate: [],
			typeOfCareId: [],
			sponsorId: [],
			legalFoundationId: [],
			caseGroupId: [],
			careRelatedHandicapId: [],
			cancellationDate: [],
			endOfCareId: [],
			documents: [maxFiles(this.maxFileCount), maxFileSize(this.maxFileSize), allowedFileTypes(this.allowedFileTypes)],
			impedimentReportDueDate: [
				this.validateOnlyWhenDirty([
					minDateValidator(this.minImpedimentReportDueDate()),
					maxDateValidator(this.maxImpedimentReportDueDate()),
				]),
			],
		};
	}

	private setValidators(name: string, validators: ValidatorFn[]) {
		const ctrl = this.form.get(name);
		if (!ctrl) return;
		ctrl.setValidators(validators);
		ctrl.updateValueAndValidity({ emitEvent: false });
	}

	private applyValidatorsForTransition() {
		const to = this.targetPhaseId();
		const from = this.fromPhaseId();
		const showCaseGroup = this.showCaseGroup();
		const base = this.baseValidators();

		Object.entries(base).forEach(([key, validators]) => this.setValidators(key, validators));

		if (!to || !from) return;

		if (to === DB_PHASES.P2) {
			this.setValidators('initiatorId', [Validators.required]);
			this.setValidators('consultationStartDate', [
				minDateValidator(this.minDateP2()),
				maxDateValidator(this.maxDate()),
				Validators.required,
			]);

			// The combination of the type of care, sponsor, legal base and case group is required in P2
			if (this.legalBaseRequiredInP2Enabled) {
				this.setValidators('typeOfCareId', [Validators.required]);
				this.setValidators('sponsorId', [Validators.required]);
				this.setValidators('legalFoundationId', [Validators.required]);
				this.setValidators('caseGroupId', showCaseGroup ? [Validators.required] : []);
			}
			if (this.careRelatedDisabilityTypeRequiredInP2Enabled) {
				this.setValidators('careRelatedHandicapId', [Validators.required]);
			}
			return;
		}

		if (to === DB_PHASES.P3) {
			this.setValidators('initiatorId', [Validators.required]);
			this.setValidators('careStartDate', [
				minDateValidator(this.minDateP3()),
				maxDateValidator(this.maxDate()),
				Validators.required,
			]);

			this.setValidators('typeOfCareId', [Validators.required]);
			this.setValidators('sponsorId', [Validators.required]);
			this.setValidators('legalFoundationId', [Validators.required]);
			this.setValidators('caseGroupId', showCaseGroup ? [Validators.required] : []);
			this.setValidators('careRelatedHandicapId', [Validators.required]);

			return;
		}

		if (to === DB_PHASES.P4) {
			this.setValidators('cancellationDate', [
				minDateValidator(this.minDateP4()),
				maxDateValidator(this.maxDate()),
				Validators.required,
			]);

			if (this.assignment()?.phaseId === DB_PHASES.P3 && this.endOfCares().length > 0) {
				this.setValidators('endOfCareId', [Validators.required]);
			}

			return;
		}
	}

	/**
	 * Returns 'true' once the data protection clarification value was already given.
	 */
	public readonly isClarificationFinal = computed(() => {
		const a = this.assignment();
		return !!(a?.hasBeenClarified && a?.clarifiedAt && a?.typeOfClarification != undefined);
	});

	private isLegacyClarification(assignment?: Assignment): boolean {
		if (!assignment) return false;

		return (
			assignment.socialDataProtectionInfo === true &&
			!assignment.hasBeenClarified &&
			!assignment.clarifiedAt &&
			assignment.typeOfClarification == null
		);
	}

	private getEffectiveClarification(assignment?: Assignment): ClarificationValue {
		if (!assignment) {
			return {
				hasBeenClarified: false,
				clarifiedAt: undefined,
				typeOfClarification: undefined,
			};
		}

		return {
			hasBeenClarified: assignment.hasBeenClarified || assignment.socialDataProtectionInfo || false,
			clarifiedAt: assignment.clarifiedAt ?? undefined,
			typeOfClarification: assignment.typeOfClarification ?? undefined,
		};
	}

	private async loadValidClarificationDateRange(assignmentId: number) {
		try {
			const range = await this.validationApi.getValidClarificationDateRange(assignmentId);
			this.clarificationDateRange.set(range);
		} catch (error) {
			console.error('Fehler beim Laden des gültigen Zeitraums für das Merkblatt Sozialdatenschutz', error);
			this.clarificationDateRange.set(undefined);
		}
	}

	private markInvalidDatesAsTouched(): void {
		[
			'cancellationDate',
			'consultationStartDate',
			'careStartDate',
			'impedimentReportDueDate',
			'unemployabilitySince',
		].forEach((field) => {
			const control = this.form.get(field);

			if (control?.invalid && !control.hasError('required')) {
				control.markAsTouched();
			}
		});
	}
}
